import React, { useState } from 'react';
import { Badge } from '../Ui';
import { ipApi } from '../../services/api';

export function downloadCsv(filename, rows, columns) {
  if (!rows?.length) return;
  const header = columns.map((c) => c.label).join(',');
  const lines = rows.map((row) => columns.map((c) => {
    const v = c.csv ? c.csv(row) : (row[c.key] ?? '');
    const s = String(v).replace(/"/g, '""');
    return `"${s}"`;
  }).join(','));
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function ExportBar({ label, rows, columns }) {
  return (
    <div className="row-actions mb" style={{ justifyContent: 'space-between' }}>
      <span className="hint">{label}</span>
      <button type="button" className="btn btn-secondary btn-sm" disabled={!rows?.length} onClick={() => downloadCsv(`${label.replace(/\s+/g, '-')}.csv`, rows, columns)}>
        Export CSV
      </button>
    </div>
  );
}

const QUEUE_STATUSES = ['waiting', 'in-consult', 'done', 'cancelled'];

export function QueueActions({ hospitalId, row, onDone }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try {
      await ipApi.platformPatchQueue(hospitalId, row.queueId, { status });
      await onDone?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row-actions">
      {QUEUE_STATUSES.map((s) => (
        <button key={s} type="button" className={`btn btn-sm ${row.status === s ? 'btn-primary' : 'btn-secondary'}`} disabled={busy} onClick={() => setStatus(s)}>
          {s}
        </button>
      ))}
    </div>
  );
}

export function PatientEditRow({ hospitalId, row, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: row.name, phone: row.phone || '', age: row.age || '', gender: row.gender || '' });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await ipApi.platformPatchPatient(hospitalId, row.patientId, {
        name: form.name.trim(),
        phone: form.phone,
        age: form.age ? Number(form.age) : undefined,
        gender: form.gender,
      });
      setOpen(false);
      await onDone?.();
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return <button type="button" className="btn btn-sm btn-secondary" onClick={() => setOpen(true)}>Edit</button>;
  }
  return (
    <div className="inline-edit-form">
      <input className="input input-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input className="input input-sm" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={save}>Save</button>
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}

export function BedAddForm({ hospitalId, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ward: 'General', room: '', bed: '', ratePerDay: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await ipApi.platformAddBed(hospitalId, {
        ward: form.ward,
        room: form.room,
        bed: form.bed,
        ratePerDay: form.ratePerDay ? Number(form.ratePerDay) : undefined,
      });
      setForm({ ward: 'General', room: '', bed: '', ratePerDay: '' });
      setOpen(false);
      await onDone?.();
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>+ Add bed</button>;
  }
  return (
    <form className="card mb form-grid bed-add-form" onSubmit={submit}>
      <div className="field"><label>Ward</label><input className="input" required value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} /></div>
      <div className="field"><label>Room</label><input className="input" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} /></div>
      <div className="field"><label>Bed</label><input className="input" value={form.bed} onChange={(e) => setForm({ ...form, bed: e.target.value })} /></div>
      <div className="field"><label>Rate/day ₹</label><input className="input" type="number" value={form.ratePerDay} placeholder="Auto from plan" onChange={(e) => setForm({ ...form, ratePerDay: e.target.value })} /></div>
      <div className="form-actions row-actions">
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>Add</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

export function BedRateEdit({ hospitalId, row, onDone }) {
  const [rate, setRate] = useState(row.ratePerDay ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await ipApi.platformPatchBed(hospitalId, row.bedId, { ratePerDay: Number(rate) });
      await onDone?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row-actions">
      <input className="input input-sm" style={{ width: 90 }} type="number" value={rate} onChange={(e) => setRate(e.target.value)} />
      <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={save}>Save</button>
    </div>
  );
}

export function LabStatusEdit({ hospitalId, row, onDone }) {
  const [busy, setBusy] = useState(false);
  const statuses = ['ordered', 'sample-collected', 'processing', 'completed', 'cancelled'];
  const setStatus = async (status) => {
    setBusy(true);
    try {
      await ipApi.platformPatchLabOrder(hospitalId, row.orderId, { status });
      await onDone?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <select className="input input-sm" disabled={busy} value={row.status || 'ordered'} onChange={(e) => setStatus(e.target.value)}>
      {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

export function PartialDataBanner({ partial, onRetry }) {
  if (!partial) return null;
  return (
    <div className="workflow-banner card mb">
      <strong>Basic mode</strong>
      <span className="hint"> Full monitor API is updating — core hospital data is shown. Tap Refresh for complete patients, queue, pharmacy, lab.</span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>Refresh all</button>
    </div>
  );
}

export function LoadErrorBanner({ error, onRetry }) {
  if (!error) return null;
  return (
    <div className="workflow-banner card mb" style={{ borderColor: '#f59e0b' }}>
      <Badge tone="warning">Notice</Badge>
      <span className="hint" style={{ marginLeft: 8 }}>{error}</span>
      <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>Retry</button>
    </div>
  );
}
